"use client"
import { Box, Typography, Paper, Container, useMediaQuery } from "@mui/material"
import { styled } from "@mui/material/styles"

// Custom styled components
const TimelineContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#3056D3", // Royal blue background color
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(4),
  color: "white",
}))

const TimelineTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 500,
  marginBottom: theme.spacing(8),
  marginTop: theme.spacing(4),
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    fontSize: "2rem",
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
}))

const TimelineWrapper = styled(Box)(({ theme, isMobile }) => ({
  position: "relative",
  width: "100%",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    left: isMobile ? "30px" : "50%",
    width: "2px",
    backgroundColor: "white",
    transform: isMobile ? "none" : "translateX(-50%)",
  },
}))

const TimelineItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  position: "relative",
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(5),
  },
}))

const TimelineNumber = styled(Paper)(({ theme, isMobile }) => ({
  width: isMobile ? 40 : 50,
  height: isMobile ? 40 : 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "white",
  color: "#000",
  fontWeight: "bold",
  fontSize: isMobile ? "1.2rem" : "1.5rem",
  zIndex: 1,
  position: "absolute",
  left: isMobile ? "30px" : "50%",
  transform: isMobile ? "translateX(-50%)" : "translateX(-50%)",
}))

const TimelineContent = styled(Box)(({ theme, isMobile }) => ({
  width: isMobile ? "calc(100% - 60px)" : "45%",
  textAlign: isMobile ? "left" : "right",
  paddingRight: isMobile ? 0 : theme.spacing(4),
  paddingLeft: isMobile ? theme.spacing(5) : 0,
  marginLeft: isMobile ? "60px" : 0,
}))

const TimelineContentRight = styled(Box)(({ theme, isMobile }) => ({
  width: isMobile ? "calc(100% - 60px)" : "45%",
  marginLeft: isMobile ? "60px" : "auto",
  textAlign: "left",
  paddingLeft: isMobile ? theme.spacing(5) : theme.spacing(4),
}))

const TimelineDate = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    fontSize: "1.2rem",
  },
}))

const TimelineDescription = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.9rem",
  },
}))

const VideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  // top: theme.spacing(4),
  width: "100%",
  maxWidth: "500px",
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
  // marginTop: theme.spacing(4),
  marginBottom: theme.spacing(8),
  [theme.breakpoints.down("lg")]: {
    maxWidth: "400px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    top: 0,
    maxWidth: "100%",
    // marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
}))

const ResponsiveIframe = styled("iframe")(({ theme }) => ({
  width: "100%",
  aspectRatio: "16/9",
  border: "none",
}))

export default function KeyDatesTimeline() {
  const isMobile = useMediaQuery("(max-width: 768px)")
  const isTablet = useMediaQuery("(max-width: 1024px)")
  const timelineData = [
    {
      number: 1,
      date: "April 2",
      description: "Registration period open for all educators.",
      isLeft: true,
    },
    {
      number: 2,
      date: "June 7",
      description: "Deadline for all deliverables.",
      isLeft: false,
    },
    {
      number: 3,
      date: "June 15",
      description: "Announcing top 10.",
      isLeft: true,
    },
    {
      number: 4,
      date: "July 5",
      description: "Summit for top 10 finalists.",
      isLeft: false,
    },
    {
      number: 5,
      date: "September",
      description: "Official Whitepaper Release featuring top solutions.",
      isLeft: true,
    },
  ]

  return (
    <TimelineContainer id="timeline" sx={{ paddingBottom: 0, paddingTop: 0 }}>
      <Container maxWidth="lg">
        <TimelineTitle variant="h1">Key Dates & Timeline</TimelineTitle>

        {isMobile || isTablet ? (
          <>
          {/* https://www.youtube.com/embed/tArPT0yyeEU */}
            <VideoContainer>
              <ResponsiveIframe
                src="https://www.youtube.com/embed/tArPT0yyeEU"
                title="Timeline Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{mb: 1}}
              />
              <ResponsiveIframe
                src="https://www.youtube.com/embed/4-wxgYdBCYc?si=ksE9bUYrngO9-EDT"
                title="Timeline Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoContainer>

            <TimelineWrapper isMobile={isMobile}>
              {timelineData.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineNumber isMobile={isMobile} elevation={3}>
                    {item.number}
                  </TimelineNumber>
                  <TimelineContent isMobile={isMobile}>
                    <TimelineDate variant="h2">{item.date}</TimelineDate>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </TimelineWrapper>
          </>
        ) : (
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Box sx={{ flex: "1 1 60%" }}>
              <TimelineWrapper isMobile={isMobile}>
                {timelineData.map((item, index) => (
                  <TimelineItem key={index}>
                    <TimelineNumber isMobile={isMobile} elevation={3}>
                      {item.number}
                    </TimelineNumber>
                    {item.isLeft ? (
                      <>
                        <TimelineContent>
                          <TimelineDate variant="h2">{item.date}</TimelineDate>
                          <TimelineDescription>{item.description}</TimelineDescription>
                        </TimelineContent>
                        <Box sx={{ width: "45%", marginLeft: "auto" }} />
                      </>
                    ) : (
                      <>
                        <Box sx={{ width: "45%" }} />
                        <TimelineContentRight>
                          <TimelineDate variant="h2">{item.date}</TimelineDate>
                          <TimelineDescription>{item.description}</TimelineDescription>
                        </TimelineContentRight>
                      </>
                    )}
                  </TimelineItem>
                ))}
              </TimelineWrapper>
            </Box>

            <Box sx={{ flex: "1 1 40%" }}>
              <VideoContainer>
                <ResponsiveIframe
                  src="https://www.youtube.com/embed/tArPT0yyeEU"
                  title="Timeline Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{mb: 2}}
                />
                <ResponsiveIframe
                  src="https://www.youtube.com/embed/4-wxgYdBCYc?si=ksE9bUYrngO9-EDT"
                  title="Timeline Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoContainer>
            </Box>
          </Box>
        )}
      </Container>
    </TimelineContainer>
  )
}

